import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { PortalShell, StatusBadge } from "@/components/business/B2BPortal";
import { SEOHead } from "@/components/shared/SEOHead";
import { useBusinessPortalSnapshot } from "@/lib/b2bPortalApi";
import {
  assetById,
  brandAssets,
  businessLocations,
  customerCatalogProducts,
  generationBriefs,
  type BrandAsset,
  type PackagingProduct,
} from "@/lib/b2bPortalData";

export const Route = createFileRoute("/business/portal/projects/new")({
  component: NewProjectPage,
});

function NewProjectPage() {
  const { data: portalData } = useBusinessPortalSnapshot();
  const catalog = customerCatalogProducts();
  const [productId, setProductId] = useState(catalog[0]?.id ?? "");
  const selectedProduct = catalog.find((product) => product.id === productId) ?? catalog[0];
  const [variantId, setVariantId] = useState(selectedProduct?.variants[0]?.id ?? "");
  const [assetId, setAssetId] = useState(brandAssets[0]?.id ?? "");
  const [quantity, setQuantity] = useState(selectedProduct?.variants[0]?.moq ?? 1000);
  const [locationId, setLocationId] = useState(businessLocations.find((location) => location.primary)?.id ?? businessLocations[0]?.id ?? "");
  const [notes, setNotes] = useState("");

  const selectedVariant = selectedProduct?.variants.find((variant) => variant.id === variantId) ?? selectedProduct?.variants[0];
  const selectedAsset = assetById(assetId);
  const selectedLocation = businessLocations.find((location) => location.id === locationId);
  const selectedZone = selectedProduct?.printZones[0];
  const minQuantity = selectedVariant?.moq ?? 0;
  const quantityReady = quantity >= minQuantity;
  const assetReady = selectedAsset?.quality === "ready";
  const orderReady = Boolean(selectedProduct && selectedVariant && selectedAsset && selectedLocation && quantityReady);

  const generatedConcepts = useMemo(() => (
    generationBriefs
      .map((brief) => ({ brief, asset: brief.outputAssetId ? assetById(brief.outputAssetId) : undefined }))
      .filter((item): item is { brief: typeof generationBriefs[number]; asset: BrandAsset } => Boolean(item.asset))
  ), []);

  const handleProductChange = (nextProductId: string) => {
    const nextProduct = catalog.find((product) => product.id === nextProductId);
    setProductId(nextProductId);
    setVariantId(nextProduct?.variants[0]?.id ?? "");
    setQuantity(nextProduct?.variants[0]?.moq ?? 1000);
  };

  return (
    <PortalShell
      active="/business/portal/projects"
      eyebrow="New order"
      title="Start a packaging order"
      description="Choose an item, artwork, quantity, and delivery location."
      account={portalData.account}
    >
      <SEOHead title="Create Packaging Project" description="Start a Quuil B2B packaging project." path="/business/portal/projects/new" robots="noindex,follow" />

      <section className="b2b-new-project-grid">
        <div className="b2b-card b2b-new-project-form">
          <label className="b2b-field">
            <span>Item</span>
            <select value={selectedProduct?.id ?? ""} onChange={(event) => handleProductChange(event.target.value)}>
              {catalog.map((product) => <option key={product.id} value={product.id}>{product.title}</option>)}
            </select>
          </label>

          <label className="b2b-field">
            <span>Option</span>
            <select value={selectedVariant?.id ?? ""} onChange={(event) => setVariantId(event.target.value)}>
              {selectedProduct?.variants.map((variant) => (
                <option key={variant.id} value={variant.id}>
                  {variant.size} · {variant.color} · {variant.finish}
                </option>
              ))}
            </select>
          </label>

          <label className="b2b-field">
            <span>Delivery</span>
            <select value={locationId} onChange={(event) => setLocationId(event.target.value)}>
              {businessLocations.map((location) => (
                <option key={location.id} value={location.id}>{location.label} · {location.city}</option>
              ))}
            </select>
          </label>

          <label className="b2b-field">
            <span>Quantity</span>
            <input type="number" min={minQuantity} step="100" value={quantity} onChange={(event) => setQuantity(Number(event.target.value))} />
          </label>

          <label className="b2b-field b2b-field-wide">
            <span>Project notes</span>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={4}
              placeholder="Timing, finish preference, or anything the proof should reflect."
            />
          </label>
        </div>

        <aside className="b2b-card b2b-start-summary">
          <span className="b2b-kicker">Summary</span>
          <h2>{selectedProduct?.title ?? "Choose packaging"}</h2>
          <dl>
            <div><dt>Option</dt><dd>{selectedVariant ? `${selectedVariant.size} · ${selectedVariant.color}` : "Required"}</dd></div>
            <div><dt>Minimum</dt><dd>{minQuantity.toLocaleString()} units</dd></div>
            <div><dt>Quantity</dt><dd>{quantity.toLocaleString()} units</dd></div>
            <div><dt>Delivery</dt><dd>{selectedLocation ? `${selectedLocation.city}, ${selectedLocation.country}` : "Required"}</dd></div>
            <div><dt>Print area</dt><dd>{selectedZone?.name ?? "Required"}</dd></div>
          </dl>
          <div className="b2b-readiness-list">
            <CheckLine label="Item selected" ready={Boolean(selectedProduct)} />
            <CheckLine label={`Minimum ${minQuantity.toLocaleString()} units`} ready={quantityReady} />
            <CheckLine label="Artwork selected" ready={Boolean(selectedAsset)} />
            <CheckLine label="Proof ready" ready={assetReady} />
          </div>
          <div className="b2b-action-row">
            <Link to="/business/portal/catalog" className="b2b-secondary-button">Back to catalog</Link>
            <Link
              to="/business/portal/projects/$projectId"
              params={{ projectId: "project-kigali-bags" }}
              className={orderReady ? "b2b-primary-button" : "b2b-primary-button is-disabled"}
              aria-disabled={!orderReady}
            >
              Review proof
            </Link>
          </div>
        </aside>
      </section>

      <section className="b2b-card">
        <div className="b2b-section-heading">
          <div>
            <span className="b2b-kicker">Artwork</span>
            <h2>Select artwork</h2>
          </div>
          <Link to="/business/portal/brands" className="b2b-secondary-button">Create artwork</Link>
        </div>
        <div className="b2b-artwork-picker-grid">
          {brandAssets.map((asset) => (
            <ArtworkButton key={asset.id} asset={asset} selected={asset.id === assetId} onSelect={() => setAssetId(asset.id)} />
          ))}
        </div>
        {generatedConcepts.length > 0 && (
          <div className="b2b-generated-choice-list">
            {generatedConcepts.map(({ brief, asset }) => (
              <article key={brief.id} className="b2b-generated-choice">
                <div>
                  <span className="b2b-kicker">Generated concept</span>
                  <strong>{brief.title}</strong>
                  <p>{brief.packagingUse} · saved as {asset.name}</p>
                </div>
                <button className="b2b-secondary-button" type="button" onClick={() => setAssetId(asset.id)}>
                  Use concept
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="b2b-card b2b-order-preview">
        <div>
            <span className="b2b-kicker">Preview</span>
            <h2>{selectedProduct?.category ?? "Packaging"} with {selectedAsset?.name ?? "selected artwork"}</h2>
          <p>You can adjust placement before requesting a quote.</p>
        </div>
        <StaticPackagingPreview product={selectedProduct} asset={selectedAsset} />
      </section>
    </PortalShell>
  );
}

function ArtworkButton({ asset, selected, onSelect }: { asset: BrandAsset; selected: boolean; onSelect: () => void }) {
  return (
    <button type="button" className={selected ? "b2b-artwork-option is-selected" : "b2b-artwork-option"} onClick={onSelect}>
      <span className={`b2b-artwork-swatch bg-gradient-to-br ${asset.previewStyle}`}>{asset.name.slice(0, 1)}</span>
      <strong>{asset.name}</strong>
      <small>{asset.fileType} · {asset.quality}</small>
    </button>
  );
}

function StaticPackagingPreview({ product, asset }: { product: PackagingProduct | undefined; asset: BrandAsset | undefined }) {
  return (
    <div className="b2b-static-preview">
      <div className={`b2b-packaging-object bg-gradient-to-br ${product?.mockupStyle ?? "from-slate-100 to-slate-300"}`}>
        <div className="b2b-packaging-seal" />
        <div className="b2b-packaging-label">{product?.category ?? "PACKAGING"}</div>
        <div className="b2b-placed-logo">
          <span>{asset?.name.slice(0, 1) ?? "Q"}</span>
          <strong>{asset?.name.replace(/ primary mark| wordmark| repeat/gi, "") ?? "BRAND ART"}</strong>
        </div>
      </div>
    </div>
  );
}

function CheckLine({ label, ready }: { label: string; ready: boolean }) {
  return (
    <div className="b2b-check-line">
      <StatusBadge status={ready ? "ready" : "review"} tone={ready ? "good" : "warn"} />
      <span>{label}</span>
    </div>
  );
}
